import React from "react";
import { Link } from "react-router-dom";
import "../../../css/MainPage/MainLanding.css";
import scholarship from "../../../assets/header-images/scholarship.jpg";

// Scholarship Guide Chapters Data
const scholarshipChapters = [
  {
    chapter: "Chapter 1",
    title: "Understanding Scholarships: Your Key to Educational Freedom",
    description:
      "Learn the fundamentals of scholarships and their importance in your educational journey.",
    topics: [
      "What is a Scholarship and Why is it Important?",
      "Types of Scholarships Available",
      "The Value Beyond Financial Aid",
    ],
    link: "/afford-college/scholarship/understanding-scholarship",
    icon: "📚",
  },
  {
    chapter: "Chapter 2",
    title: "Preparing for the Scholarship Search: A Strategic Approach",
    description:
      "Develop a systematic approach to finding and organizing scholarship opportunities.",
    topics: [
      "Establishing Your Scholarship Goals",
      "Navigating Timelines and Application Periods",
      "Effective Scholarship Research Strategies",
    ],
    link: "/afford-college/scholarship/scholarship-search-guide",
    icon: "🔍",
  },
  {
    chapter: "Chapter 3",
    title: "Maximizing Your Chances of Success: Strategic Tips for Winning",
    description:
      "Learn proven strategies to increase your scholarship success rate.",
    topics: [
      "Prioritize Local Scholarships",
      "Avoiding Common Pitfalls and Scams",
      "Organization and Time Management",
      "The Power of Persistence",
    ],
    link: "/afford-college/scholarship/application-tips",
    icon: "🎯",
  },
  {
    chapter: "Chapter 4",
    title: "Crafting a Standout Application: Showcase Your Strengths and Shine",
    description:
      "Master the art of creating compelling scholarship applications that get noticed.",
    topics: [
      'The "Information About Yourself" Template',
      "Steps to Craft a Compelling Scholarship Essay",
      "Writing Essays That Win",
      "Securing Strong Recommendation Letters",
    ],
    link: "/afford-college/scholarship/crafting-application",
    icon: "✍️",
  },
  {
    chapter: "Chapter 5",
    title: "Mastering the Scholarship Interview: Confidence is Key",
    description:
      "Prepare for scholarship interviews with confidence and professionalism.",
    topics: [
      "Understanding the Interview Process",
      "Professional Attire: The Silent Communicator",
      "Strategies for Interview Success",
    ],
    link: "/afford-college/scholarship/scholarship-interview",
    icon: "🎤",
  },
  {
    chapter: "Chapter 6",
    title: "Post-Award Success Strategies: How to Maximize the Benefits",
    description:
      "Learn how to manage and maximize your scholarship awards effectively.",
    topics: [
      "Expressing Gratitude",
      "Effective Management of Scholarship Funds",
      "Appeal for Cost of Attendance Increase",
    ],
    link: "/afford-college/scholarship/post-award-tips",
    icon: "🏆",
  },
];

// Recommended Scholarship Resources
const scholarshipResources = [
  {
    name: "CollegeNav's Top Choices Spreadsheet",
    description: "Year-round scholarships curated by our team",
    url: "https://docs.google.com/spreadsheets/d/1_g5FWjag24UN9Fp7Jz3AbrUYVP7XKn71su9fCK5-pKM/edit?usp=sharing",
    type: "Curated List",
  },
  {
    name: "Scholarship America",
    description: "One of the largest scholarship providers in the US",
    url: "https://www.scholarshipamerica.org",
    type: "Organization",
  },
  {
    name: "Kaleidoscope",
    description: "Scholarships for underrepresented students",
    url: "https://www.mykaleidoscope.com",
    type: "Platform",
  },
  {
    name: "College Board Scholarship Search",
    description: "Comprehensive scholarship database",
    url: "https://bigfuture.collegeboard.org/scholarship-search",
    type: "Database",
  },
];

const Scholarship = () => {
  const introText = `Securing scholarships can significantly ease the financial burden of college, allowing you to focus on your studies and personal growth. This comprehensive guide provides detailed, step-by-step information on finding and applying for scholarships. Whether you're just beginning your scholarship search or looking to refine your application strategy, you'll find valuable tips and resources here to help you succeed.`;

  return (
    <div className="main-landing-page">
      {/* Hero Section */}
      <div
        className="main-landing-hero"
        style={{ backgroundImage: `url(${scholarship})` }}
      >
        <div className="main-landing-hero-overlay" />
        <div className="main-landing-hero-content">
          <h1>Scholarship Guide</h1>
          <div className="main-landing-hero-sub">
            Your complete guide to finding and winning scholarships for college
          </div>
        </div>
      </div>

      {/* Introduction Section */}
      <div className="main-landing-mission-section">
        <div className="main-landing-mission-container">
          <h2>Master the Art of Scholarship Success</h2>
          <p>{introText}</p>
        </div>
      </div>

      {/* Scholarship Guide Chapters */}
      <div className="main-landing-section">
        <div className="main-landing-section-title">
          Scholarship Guide Chapters
        </div>
        <div className="main-landing-grid">
          {scholarshipChapters.map((chapter, idx) => (
            <Link key={idx} to={chapter.link} className="main-landing-card">
              <div className="main-landing-card-info">
                <div className="scholarship-chapter-header">
                  <span className="scholarship-icons">{chapter.icon}</span>
                  <span className="scholarship-chapter-number">
                    {chapter.chapter}
                  </span>
                </div>
                <div className="main-landing-card-title">{chapter.title}</div>
                <div className="main-landing-card-desc">
                  {chapter.description}
                </div>

                <div className="scholarship-topics">
                  <h4>What You'll Learn:</h4>
                  <ul>
                    {chapter.topics.map((topic, topicIdx) => (
                      <li key={topicIdx}>{topic}</li>
                    ))}
                  </ul>
                </div>

                <div className="main-landing-link-btn">
                  Start Chapter
                  <svg
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7 5l5 5-5 5"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Scholarship Resources Section */}
      <div
        className="main-landing-section"
        style={{ background: "var(--white)" }}
      >
        <div className="main-landing-section-title">
          Recommended Scholarship Resources
        </div>
        <div
          className="main-landing-mission-container"
          style={{ marginBottom: "40px" }}
        >
          <p style={{ textAlign: "center" }}>
            Start with local scholarships first, then expand to national
            opportunities. Here are our curated resources to help you find the
            best scholarships for your situation.
          </p>
        </div>

        <div className="scholarship-resources-grid">
          {scholarshipResources.map((resource, idx) => (
            <div
              key={idx}
              className={`scholarship-resource-card ${
                resource.featured ? "featured" : ""
              }`}
            >
              <div className="resource-header">
                <h3>{resource.name}</h3>
                <span className="resource-type">{resource.type}</span>
              </div>
              <p className="resource-description">{resource.description}</p>
              <a
                href={resource.url}
                target="_blank"
                rel="noopener noreferrer"
                className="resource-link"
              >
                Visit Resource
                <svg
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z"
                    fill="currentColor"
                  />
                  <path
                    d="M5 5a2 2 0 00-2 2v6a2 2 0 002 2h6a2 2 0 002-2v-2a1 1 0 10-2 0v2H5V7h2a1 1 0 000-2H5z"
                    fill="currentColor"
                  />
                </svg>
              </a>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Tips Section */}
      <div className="main-landing-section">
        <div className="main-landing-section-title">Quick Success Tips</div>
        <div className="scholarship-tips-container">
          <div className="scholarship-tip">
            <div className="tip-icon">🏠</div>
            <h3>Start Local</h3>
            <p>
              Local scholarships often have fewer applicants and higher success
              rates
            </p>
          </div>
          <div className="scholarship-tip">
            <div className="tip-icon">📅</div>
            <h3>Apply Early</h3>
            <p>
              Submit applications well before deadlines to avoid last-minute
              issues
            </p>
          </div>
          <div className="scholarship-tip">
            <div className="tip-icon">📝</div>
            <h3>Personalize Essays</h3>
            <p>
              Tailor each essay to the specific scholarship and organization
            </p>
          </div>
          <div className="scholarship-tip">
            <div className="tip-icon">🔄</div>
            <h3>Stay Persistent</h3>
            <p>Apply to multiple scholarships - it's a numbers game</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Scholarship;
